/**
 * Storage formatting utilities
 * Handles conversion from bytes to human-readable format
 */

export interface StorageFormat {
  value: number;
  formattedValue: string;
  unit: 'KB' | 'MB' | 'GB' | 'TB';
  fullText: string;
}

/**
 * Convert bytes to the most appropriate storage unit
 * @param bytes - Storage size in bytes
 * @returns Formatted storage object with value, unit, and full text
 */
export const formatStorageFromBytes = (bytes: number): StorageFormat => {
  if (bytes === 0) {
    return {
      value: 0,
      formattedValue: '0.0',
      unit: 'GB',
      fullText: '0 GB'
    };
  }

  const GB = 1;
  const MB = GB / 1024;
  const TB = GB * 1024;

  let value: number;
  let unit: 'KB' | 'MB' | 'GB' | 'TB';

  if (bytes < MB) {
    // Less than 1 MB, show in KB
    value = bytes * Math.pow(1024,2);
    unit = 'KB';
  } else if (bytes < GB) {
    // Less than 1 GB, show in MB
    value = bytes * Math.pow(1024,1);
    unit = 'MB';
  } else if (bytes < TB) {
    // Less than 1 TB, show in GB
    value = bytes;
    unit = 'GB';
  } else {
    // 1 TB or more
    value = bytes / 1024;
    unit = 'TB';
  }

  const formattedValue = value.toFixed(1);

  return {
    value,
    formattedValue,
    unit,
    fullText: `${formattedValue} ${unit}`
  };
};

/**
 * Format storage for display with smart unit handling
 * Shows unit for used value only when it differs from limit unit (which is always GB)
 *
 * @param usedBytes - Used storage in bytes
 * @param limitGB - Limit storage in GB
 * @returns Object with formatted components for display
 */
export const formatStorageDisplay = (
  usedBytes: number,
  limitGB: number
): {
  usedValue: string;
  usedUnit: string;
  limitValue: string;
  limitUnit: string;
  shouldShowUsedUnit: boolean;
} => {
  const usedFormat = formatStorageFromBytes(usedBytes);
  const limitValue = limitGB.toFixed(1);
  const limitUnit = 'GB';

  // Only show used unit when it differs from GB
  const shouldShowUsedUnit = usedFormat.unit !== 'GB';

  return {
    usedValue: usedFormat.formattedValue,
    usedUnit: usedFormat.unit,
    limitValue,
    limitUnit,
    shouldShowUsedUnit
  };
};

/**
 * Simple format for display string
 * Example outputs:
 * - "512 MB / 10 GB" (when used < 1 GB)
 * - "5.2 / 10 GB" (when used >= 1 GB, unit not repeated)
 *
 * @param usedBytes - Used storage in bytes
 * @param limitGB - Limit storage in GB
 * @returns Formatted display string
 */
export const formatStorageDisplayString = (
  usedBytes: number,
  limitGB: number
): string => {
  const { usedValue, usedUnit, limitValue, limitUnit, shouldShowUsedUnit } =
    formatStorageDisplay(usedBytes, limitGB);

  if (shouldShowUsedUnit) {
    // Different units: "512 MB / 10 GB"
    return `${usedValue} ${usedUnit} / ${limitValue} ${limitUnit}`;
  } else {
    // Same unit (GB): "5.2 / 10 GB"
    return `${usedValue} / ${limitValue} ${limitUnit}`;
  }
};
